// ─── Types ────────────────────────────────────────────────────────────────────

import { UseMutationOptions, useMutation } from 'react-query';

export interface IDocumentBaseRequestModel {
    entityGuid: string;
    entityTypeId: number;
    noteTypeId?: number | null;
    strategyTypeId?: number | null;
}

export interface IDocumentDownloadRequestModel extends IDocumentBaseRequestModel {
    documentVersionGuid: string;
    tenantGuid: string;
}

export interface IUseDocumentDownloadOptions
    extends Omit<UseMutationOptions<void, Error, IDocumentDownloadRequestModel>, 'mutationFn'> {
    endpoint: string;
    filename?: string;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function extractFilenameFromHeader(contentDisposition: string | null): string | null {
    if (!contentDisposition) return null;

    const utf8Match = contentDisposition.match(/filename\*=(?:UTF-8'')?([^;]+)/i);
    if (utf8Match) return decodeURIComponent(utf8Match[1].trim());

    const asciiMatch = contentDisposition.match(/filename="?([^";]+)"?/i);
    if (asciiMatch) return asciiMatch[1].trim();

    return null;
}

async function downloadDocument(
    request: IDocumentDownloadRequestModel,
    endpoint: string,
    fallbackFilename?: string
): Promise<void> {
    const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'X-Tenant-Guid': request.tenantGuid,
        },
        body: JSON.stringify(request),
    });

    if (!response.ok) {
        throw new Error(`Download failed with status ${response.status}: ${response.statusText}`);
    }

    const blob = await response.blob();

    const filename =
        fallbackFilename ??
        extractFilenameFromHeader(response.headers.get('Content-Disposition')) ??
        'document';

    const objectUrl = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = objectUrl;
    anchor.download = filename;
    anchor.style.display = 'none';
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);

    setTimeout(() => URL.revokeObjectURL(objectUrl), 10_000);
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

/**
 * @example
 * const { download, isDownloading } = useDocumentDownload({
 *   endpoint: props.downloadUrl,
 *   onError: (err) => showErrorToast(err.message),
 * });
 *
 * download({
 *   entityGuid: props.entityGuid,
 *   entityTypeId: props.entityTypeId,
 *   documentVersionGuid: doc.versionGuid,
 *   tenantGuid: zoneGuid,
 * });
 */
export function useDocumentDownload({
    endpoint,
    filename,
    ...mutationOptions
}: IUseDocumentDownloadOptions) {
    const mutation = useMutation<void, Error, IDocumentDownloadRequestModel>({
        mutationFn: (request) => downloadDocument(request, endpoint, filename),
        ...mutationOptions,
    });

    return {
        download: mutation.mutate,
        downloadAsync: mutation.mutateAsync,
        isDownloading: mutation.isLoading,
        isSuccess: mutation.isSuccess,
        isError: mutation.isError,
        error: mutation.error,
        reset: mutation.reset,
    };
}
